import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Typography,
  Grid2,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  TablePagination,
  CircularProgress,
  Chip,
} from "@mui/material";
import { toast } from "sonner";
import { Iconify } from "../Minimal/src/components/iconify";
import { Link, useNavigate } from 'react-router-dom';
import { API_URL, BASE_URL } from '../Config';
import config from '../common/Config';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import '../common/commonStyles.css';

const PurchasesOrder = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ordered');
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem("token");
  const company_tax_type = localStorage.getItem('company_tax_type');

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [actionRow, setActionRow] = useState(null);

  const getEndpoint = (tab) => {
    if (tab === 'received') {
      return `${API_URL}/purchases/grnlist_received`;
    } else if (tab === 'pending') {
      return `${API_URL}/purchases/grnlist_pending`;
    } else {
      return `${API_URL}/purchases/grnlist_ordered`;
    }
  };

  const getStatusLabel = (status) => {
    if (status === 'Draft') return 'Ordered';
    if (status === 'Partial') return 'Pending';
    if (status === 'Close') return 'Received';
    return status;
  };

  const fetchPurchaseOrders = async () => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    setLoading(true);

    try {
      const endpoint = getEndpoint(activeTab);
      const params = new URLSearchParams({
        draw: '1',
        start: (page * rowsPerPage).toString(),
        length: rowsPerPage.toString(),
        search: searchQuery,
      });

      const response = await fetch(`${endpoint}?${params}`, {
        method: 'GET',
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch purchase orders');
      }

      const json = await response.json();

      if (json.error) {
        toast.error(json.error);
        setPurchaseOrders([]);
        setTotalCount(0);
      } else {
        setPurchaseOrders(Array.isArray(json.data) ? json.data : []);
        setTotalCount(json.recordsTotal || json.recordsFiltered || json.data?.length || 0);
      }
    } catch (err) {
      toast.error('Failed to load purchase orders');
      setPurchaseOrders([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchaseOrders();
  }, [activeTab, page, rowsPerPage, searchQuery]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleActionMenuOpen = (event, row) => {
    setActionAnchorEl(event.currentTarget);
    setActionRow(row);
  };

  const handleActionMenuClose = () => {
    setActionAnchorEl(null);
    setActionRow(null);
  };

  const getActionMenuItems = (row) => {
    const items = [];

    if (row.status === 'Draft') {
      items.push({
        label: 'View PO',
        icon: 'eva:eye-fill',
        href: `${BASE_URL}/purchases/view_po/${row.id}`,
      });
    } else {
      items.push({
        label: 'View GRN',
        icon: 'eva:eye-fill',
        href: `${BASE_URL}/purchases/view/${row.id}`,
      });
    }

    if (row.status === 'Draft') {
      items.push({
        label: 'Edit PO',
        icon: 'eva:edit-fill',
        href: `${BASE_URL}/purchases/edit_po/${row.id}`,
      });
      items.push({
        label: 'Add GRN',
        icon: 'eva:file-add-fill',
        href: `${BASE_URL}/purchases/add_grn/${row.id}`,
      });
    } else if (row.status === 'Partial') {
      items.push({
        label: 'Print Invoice',
        icon: 'eva:printer-fill',
        href: `${config.BASE_URL}/purchaseOrder/invoice/${row.id}`,
      });
      items.push({
        label: 'Add GRN',
        icon: 'eva:file-add-fill',
        href: `${BASE_URL}/purchases/add_grn/${row.id}`,
      });
    } else {
      items.push({
        label: 'View PO',
        icon: 'eva:file-text-fill',
        href: `${BASE_URL}/purchases/view_po/${row.id}`,
      });
      items.push({
        label: 'Print Invoice',
        icon: 'eva:printer-fill',
        href: `${config.BASE_URL}/purchaseOrder/invoice/${row.id}`,
      });
    }

    return items;
  };

  const fetchProductPrint = async (pdtId) => {
    const formData = new FormData();
    formData.append('id', pdtId);
    try {
      const response = await fetch(`${API_URL}/purchases/purchases_print`, {
        method: 'POST',
        headers: { authorization: `Bearer ${accessToken}` },
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to fetch order details');
      const data = await response.json();
      generatePDF(data);
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const generatePDF = (data) => {
    const doc = new jsPDF();
    const header = [
      [`Order Summary for ${data.shop_details.shop_name}`, `Address: ${data.shop_details.address}`],
      [`COMPANY REG NO: ${data.shop_details.company_reg_no}`, `Email: ${data.shop_details.email}`],
    ];
    const header2 = [
      [`${data.purchases_details.name}`, `${data.purchases_details.reference_no}`],
      [`PO No: ${data.purchases_details.reference_no}`, `Date: ${data.purchases_details.date}`],
      [`Cust Account No: ${data.purchases_details.po_id}`, `Due Date: ${data.purchases_details.date}`],
    ];
    const columns = [
      { title: 'Description', dataKey: 'description' },
      { title: 'Quantity', dataKey: 'quantity' },
    ];
    const tableData = data.purchases_items.map(item => ({
      description: `${item.product_code} - ${item.product_name}`,
      quantity: parseInt(item.quantity, 10),
    }));

    doc.autoTable({ head: header, startY: 10 });
    doc.autoTable({ head: header2, startY: doc.lastAutoTable.finalY + 5 });
    doc.autoTable({ columns, body: tableData, startY: doc.lastAutoTable.finalY + 10 });
    doc.save(`invoice_${Date.now()}.pdf`);
  };

  const getColumns = () => {
    if (activeTab === 'received') {
      return [
        { id: 'date', label: 'Order Date', width: 120 },
        { id: 'receive_date', label: 'Receive Date', width: 120 },
        { id: 'reference_no', label: 'PO No', width: 120 },
        { id: 'supplier', label: 'Supplier', width: 200 },
        { id: 'total', label: 'Net Total', width: 120, numeric: true },
        { id: 'total_tax', label: company_tax_type, width: 120, numeric: true },
        { id: 'grand_total', label: 'Grand Total', width: 120, numeric: true },
        { id: 'status', label: 'Status', width: 100 },
        { id: 'receive_name', label: 'Receive Name', width: 150 },
        { id: 'action', label: 'Action', width: 80 },
      ];
    } else {
      return [
        { id: 'date', label: 'Date', width: 120 },
        { id: 'reference_no', label: 'PO No', width: 120 },
        { id: 'supplier', label: 'Supplier', width: 200 },
        { id: 'total', label: 'Net Total', width: 120, numeric: true },
        { id: 'total_tax', label: company_tax_type, width: 120, numeric: true },
        { id: 'grand_total', label: 'Grand Total', width: 120, numeric: true },
        { id: 'status', label: 'Status', width: 100 },
        { id: 'action', label: 'Action', width: 80 },
      ];
    }
  };

  const columns = getColumns();

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={1} alignItems="center" justifyContent="space-between">
              <Grid2 size={{ xs: 12, md: 7 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    className="common-tabs"
                  >
                    <Tab label="Ordered" value="ordered" />
                    <Tab label="Received" value="received" />
                    <Tab label="Pending" value="pending" />
                  </Tabs>
                </Box>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "stretch", md: "flex-end" },
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <TextField
                    placeholder="Search..."
                    size="small"
                    className="input-border"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Iconify icon="eva:search-outline" />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setSearchQuery("")}
                              sx={{ color: "#637381" }}
                            >
                              <Iconify icon="eva:close-fill" width={18} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }
                    }}
                    sx={{
                      flex: { xs: "1 1 100%", md: "0 1 280px" },
                      minWidth: { md: 220 },
                    }}
                  />
                  <Button
                    component={Link}
                    className="button-variant-contained"
                    to="/purchases/add"
                    variant="contained"
                    sx={{
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                    }}
                  >
                    Add Purchase Order
                  </Button>
                </Box>
              </Grid2>
            </Grid2>
          </Box>
          <Box>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 0,
                border: "1px solid rgba(145, 158, 171, 0.16)",
                overflow: "hidden",
                backgroundColor: "#fff",
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table
                      className="table-cell-border"
                      size="medium"
                      sx={{ tableLayout: "fixed" }}
                    >
                      <TableHead
                      >
                        <TableRow className="table-cell-header">
                          {columns.map((column) => (
                            <TableCell
                              key={column.id}
                              sx={{

                                width: column.width,
                                minWidth: column.width,
                                textAlign: column.numeric ? "right" : "left",
                              }}
                            >
                              {column.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {purchaseOrders.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={columns.length} sx={{ textAlign: "center", py: 8 }}>
                              <Typography>
                                No data available
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          purchaseOrders.map((row) => (
                            <TableRow
                              key={row.id}
                              hover
                              sx={{
                                "&:hover": {
                                  backgroundColor: "rgba(145, 158, 171, 0.04)"
                                },

                                transition: "background-color 0.2s ease"
                              }}
                            >
                              {columns.map((column) => {
                                if (column.id === 'action') {
                                  return (
                                    <TableCell key={column.id} sx={{ fontSize: "0.75rem", color: "#212b36", py: 1 }}>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => handleActionMenuOpen(e, row)}
                                        sx={{ color: "#637381" }}
                                      >
                                        <Iconify icon="eva:more-vertical-fill" width={20} />
                                      </IconButton>
                                    </TableCell>
                                  );
                                }

                                if (column.id === 'status') {
                                  const statusLabel = getStatusLabel(row.status);
                                  const statusColor = row.status === 'Draft' ? 'warning' : row.status === 'Partial' ? 'info' : 'success';
                                  return (
                                    <TableCell key={column.id} sx={{ fontSize: "0.75rem", color: "#212b36", py: 1 }}>
                                      <Chip
                                        label={statusLabel}
                                        color={statusColor}
                                        variant="soft"
                                        size="small"
                                        sx={{
                                          fontSize: "0.75rem",
                                          height: 24,
                                          fontWeight: 500,
                                        }}
                                      />
                                    </TableCell>
                                  );
                                }

                                let cellValue = row[column.id] || "";
                                if (column.numeric && cellValue) {
                                  cellValue = parseFloat(cellValue).toFixed(2);
                                }

                                return (
                                  <TableCell
                                    key={column.id}
                                    className="table-cell"
                                    sx={{
                                      textAlign: column.numeric ? "right" : "left",
                                      fontWeight: column.numeric ? 500 : 400,
                                    }}
                                  >
                                    {cellValue}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box display="flex" justifyContent="flex-end" alignItems="center">
                    <TablePagination
                      component="div"
                      count={totalCount}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}
                      className="pagination-styles"
                    />
                  </Box>
                </>
              )}
            </Paper>
          </Box>
        </Paper>
      </Box>
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleActionMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        MenuListProps={{
          sx: {
            maxHeight: 'calc(100vh - 100px)',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
          },
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            mt: 0.5,
          },
        }}
        disableScrollLock={false}
        autoFocus={false}
      >
        {actionRow && getActionMenuItems(actionRow).map((item, index) => (
          <MenuItem
            key={index}
            onClick={() => {
              handleActionMenuClose();
              if (item.href.startsWith('http') || item.href.startsWith(config.BASE_URL)) {
                window.open(item.href, '_blank');
              } else {
                navigate(item.href.replace(BASE_URL, ''));
              }
            }}
            sx={{
              fontSize: "0.875rem",
              py: 1,
            }}
          >
            <Iconify icon={item.icon} width={18} sx={{ mr: 1.5, color: "#637381" }} />
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default PurchasesOrder;
